import React, { useEffect, useMemo, useState } from 'react';
import { Segmented, Select } from 'antd';
import HStack from './Layout/HStack';
import VStack from './Layout/VStack';
import DropDownMenu from './DropDownMenu';
import SelectChips from './SelectChips';
import AppText from './AppText';
import { Colors } from '../../styles/Colors';
import { GlobalStyleAttributes } from '../../styles/GlobalStyles';
import { isMobile } from '../../services/Helpers';
import './AntUI.scss';

// config item shape:
// {
//   key: string,
//   show: boolean,
//   defaultValue: any,
//   type?: 'select' | 'segmented' | 'dropdown' | 'chips',  — default 'select'
//   options: { value, label }[] | (baseList: T[]) => { value, label }[],  — function only when `list` is passed
//   filterFunc?: (item: T, value: any) => boolean,  — required when `list` is passed
//   style?: CSSProperties,
//   showSearch?: boolean,
//   title?: ReactNode,       — dropdown: fixed trigger label (overrides selected option label)
//   sectionTitle?: ReactNode, — label above the control (shown on mobile only)
//   placeholder?: string,    — dropdown: fallback trigger label
//   props?: object,          — spread onto the rendered control (className, styleOverride, showBorder, …)
// }

const FiltersHandler = ({ list, config, onFiltered, values, onChange, containerProps }) => {
    const [filterValues, setFilterValues] = useState(() =>
        Object.fromEntries(config.map(cfg => [cfg.key, cfg.defaultValue]))
    );

    const currentValues = values ?? filterValues;

    const resolvedOptions = useMemo(() =>
        Object.fromEntries(config.map(cfg => {
            if (typeof cfg.options === 'function') {
                if (!list) return [cfg.key, []];
                const otherConfigs = config.filter(c => c.key !== cfg.key);
                const baseList = list.filter(item =>
                    otherConfigs.every(c => c.filterFunc(item, currentValues[c.key]))
                );
                return [cfg.key, cfg.options(baseList)];
            }
            return [cfg.key, cfg.options];
        })),
    [list, config, currentValues]);

    const filteredList = useMemo(() => {
        if (!list || !onFiltered) return null;
        return list.filter(item => config.every(cfg => cfg.filterFunc(item, currentValues[cfg.key])));
    }, [list, config, currentValues, onFiltered]);

    useEffect(() => {
        if (filteredList == null) return;
        onFiltered(filteredList, currentValues);
    }, [filteredList]);

    const onFilterChange = (key, value) => {
        setFilterValues(prev => ({ ...prev, [key]: value }));
        onChange?.(key, value);
    };

    const renderFilter = (cfg) => {
        const filterType = cfg.type ?? 'select';
        const controlProps = cfg.props ?? {};

        if (filterType === 'segmented') {
            return (
                <Segmented
                    key={cfg.key}
                    options={resolvedOptions[cfg.key]}
                    value={currentValues[cfg.key]}
                    onChange={(val) => onFilterChange(cfg.key, val)}
                    {...controlProps}
                />
            );
        }

        if (filterType === 'dropdown') {
            const options = resolvedOptions[cfg.key] ?? [];
            const selectedLabel = options.find((opt) => opt.value === currentValues[cfg.key])?.label;
            const title = cfg.title ?? selectedLabel ?? cfg.placeholder;
            const items = options.map((opt) => ({ id: opt.value, name: opt.label }));

            return (
                <DropDownMenu
                    key={cfg.key}
                    items={items}
                    title={title}
                    onPress={(val) => onFilterChange(cfg.key, val)}
                    {...controlProps}
                />
            );
        }

        if (filterType === 'chips') {
            return (
                <SelectChips
                    key={cfg.key}
                    options={resolvedOptions[cfg.key] ?? []}
                    selectedValue={currentValues[cfg.key]}
                    onChange={(val) => onFilterChange(cfg.key, val)}
                    {...controlProps}
                />
            );
        }

        return (
            <Select
                key={cfg.key}
                className="filters-handler-select"
                popupClassName="filters-handler-select-dropdown"
                options={resolvedOptions[cfg.key]}
                value={currentValues[cfg.key]}
                onChange={(val) => onFilterChange(cfg.key, val)}
                style={cfg.style}
                showSearch={cfg.showSearch}
                optionFilterProp={cfg.showSearch ? 'label' : undefined}
                {...controlProps}
            />
        );
    };

    const renderFilterWithTitle = (cfg) => {
        if (!cfg.sectionTitle || !isMobile) return renderFilter(cfg);
        return (
            <VStack key={cfg.key} space="8px" alignItems="flex-start">
                <AppText fontSize={GlobalStyleAttributes.fonts.heading4} color={Colors.newDesign.darkGrey} boldFont>
                    {cfg.sectionTitle}
                </AppText>
                {renderFilter(cfg)}
            </VStack>
        );
    };

    return (
        <HStack space={'0.75rem'} flexWrap={'wrap'} {...containerProps}>
            {config.filter(cfg => cfg.show).map(renderFilterWithTitle)}
        </HStack>
    );
};

export default FiltersHandler;
